import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/*
 * Back to top.
 *
 * Rendered once by the router's layout route, not per page, so it is chrome the
 * way <ScrollRestoration> is chrome: every page is long enough to want it, and
 * a control that appeared on one of them and not the others would read as a
 * quirk of that page rather than as something the site does.
 *
 * A <button>, deliberately, and not a <Link> to the top. A link would push a
 * history entry, so the reader who used it and then pressed Back would land
 * where they were rather than where Back means — and this site has taken scroll
 * restoration off the browser, which makes that entry ours to get right for no
 * benefit. Returning to the top is a scroll, not a navigation.
 */

// One full screen of scrolling. Any less and the button is in the way on a
// page the reader has barely moved through; the control only earns its space
// once going back by hand would be a real journey.
const SHOW_AFTER_VIEWPORTS = 1;

// Where the keyboard lands afterwards. Without this the reader who pressed
// Enter keeps focus on a button that has just removed itself from the page, and
// the next Tab resumes from the end of the document — the top of the page
// arrives for the eye and not for the keyboard. The brand link is the first
// focusable thing on every page, so focusing it puts the reader at the start of
// the tab order, which is what "back to top" means to a keyboard.
const FOCUS_TARGET = ".navbar-brand";

function BackToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const update = () => {
      // Setting the same boolean again is a no-op in React, so the scroll
      // handler costs one comparison per frame rather than a render.
      setVisible(window.scrollY > window.innerHeight * SHOW_AFTER_VIEWPORTS);
    };

    // Not only on scroll: a restored position or a rotated phone can put the
    // reader past the threshold without one ever firing.
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  if (!visible) return null;

  const handleClick = () => {
    const target = document.querySelector(FOCUS_TARGET);
    // preventScroll, because focusing an element scrolls it into view instantly
    // — which would land at the top a frame before the smooth scroll below had
    // started, and the reader would see the page jump and then glide.
    if (target) target.focus({ preventScroll: true });

    // Two arguments rather than `behavior`, so the stylesheet decides how this
    // moves: `html { scroll-behavior: smooth }` normally, and `auto` under
    // prefers-reduced-motion, where _service-detail.scss already turns it off.
    // Passing "smooth" here would override the reader's preference; passing
    // "instant" would override the site's.
    window.scrollTo(0, 0);
  };

  return (
    <button
      type="button"
      className="back-to-top"
      onClick={handleClick}
      // The arrow is the whole control, so the name has to come from here.
      aria-label={t("nav.backToTop")}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M8 13.25V3.75M3.5 8.25L8 3.75l4.5 4.5"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export default BackToTop;
